import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import i18next from 'i18next';

const CACHE_PREFIX = 'help_content_cache_v3_';
const CACHE_TTL = 24 * 60 * 60 * 1000;

const HelperContext = createContext(null);

const getCurrentLanguage = (language) => {
  return String(
    language ||
    i18next.language ||
    localStorage.getItem('i18nextLng') ||
    localStorage.getItem('language') ||
    'hu'
  ).split('-')[0];
};

const normalizeCachePart = (value) => {
  return (value || 'all').toString().replace(/[^a-zA-Z0-9_-]/g, '-');
};

const getUserCacheScope = () => {
  return [
    localStorage.getItem('networkId') || 'no-network',
    localStorage.getItem('userName') || localStorage.getItem('employeeId') || 'user',
    localStorage.getItem('roles') || 'roles',
  ].map(normalizeCachePart).join('_');
};

const getStorageCacheKey = ({ module, screen, section, language }) => {
  return CACHE_PREFIX + [
    getUserCacheScope(),
    normalizeCachePart(module),
    normalizeCachePart(screen),
    normalizeCachePart(section),
    normalizeCachePart(language || 'hu'),
  ].join('_');
};

const getMemoryCacheKey = ({ module, screen, section, language }) => {
  return [
    getUserCacheScope(),
    normalizeCachePart(module),
    normalizeCachePart(screen),
    normalizeCachePart(section),
    normalizeCachePart(language || 'hu'),
  ].join('_');
};

const addSearchParam = (params, key, value) => {
  if (!value) {
    return;
  }

  params.append(`search[${key}][operator]`, '=');
  params.append(`search[${key}][value]`, value);
};

const getResponseItems = (response) => {
  const data = response?.data?.data || response?.data;
  return data?.items || data || [];
};

const getResponseMeta = (response, fallbackItems = []) => {
  const data = response?.data?.data || response?.data;

  return {
    page: Number(data?.page || 1),
    itemPerPage: Number(data?.item_per_page || fallbackItems.length || 0),
    totalItemCount: Number(data?.total_item_count || fallbackItems.length || 0),
  };
};

const HelperService = {
  getHelpContentResponse({
    module,
    screen,
    section,
    language,
    page = 1,
    itemPerPage = 100,
    forceRefresh = false,
  } = {}) {
    if (!localStorage.getItem('userToken')) {
      return Promise.resolve({
        items: [],
        meta: {
          page: 1,
          itemPerPage,
          totalItemCount: 0,
        },
      });
    }

    const currentLanguage = getCurrentLanguage(language);
    const cacheKey = getStorageCacheKey({
      module,
      screen,
      section: [section, page, itemPerPage].filter(Boolean).join('_'),
      language: currentLanguage,
    });
    const cached = localStorage.getItem(cacheKey);

    if (!forceRefresh && cached) {
      try {
        const parsed = JSON.parse(cached);

        if (
          parsed &&
          parsed.createdAt &&
          Date.now() - Number(parsed.createdAt) < CACHE_TTL
        ) {
          return Promise.resolve({
            items: parsed.items || [],
            meta: parsed.meta || {
              page,
              itemPerPage,
              totalItemCount: (parsed.items || []).length,
            },
          });
        }
      } catch (e) {
        localStorage.removeItem(cacheKey);
      }
    }

    const params = new URLSearchParams();

    addSearchParam(params, 'module_code', module);
    addSearchParam(params, 'screen', screen);
    addSearchParam(params, 'section', section);
    addSearchParam(params, 'language', currentLanguage);
    params.append('page', page);
    params.append('item_per_page', itemPerPage);

    const queryString = params.toString();
    const url = process.env.REACT_APP_API_HOST + '/api/v2/help-content' + (queryString ? '?' + queryString : '');

    return axios.get(
      url,
      {
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('userToken'),
          'App-Network': localStorage.getItem('networkId') || '',
          'Accept-Language': currentLanguage,
        },
      }
    ).then((response) => {
      const items = getResponseItems(response);
      const meta = getResponseMeta(response, items);

      localStorage.setItem(cacheKey, JSON.stringify({
        createdAt: Date.now(),
        items,
        meta,
      }));

      return {
        items,
        meta,
      };
    });
  },

  getHelpContents(params = {}) {
    return HelperService.getHelpContentResponse(params).then((response) => response.items);
  },

  getAllHelpContents({
    module,
    screen,
    section,
    language,
    itemPerPage = 100,
    forceRefresh = false,
  } = {}) {
    const loadPage = (page, collectedItems = []) => {
      return HelperService.getHelpContentResponse({
        module,
        screen,
        section,
        language,
        page,
        itemPerPage,
        forceRefresh,
      }).then((response) => {
        const nextItems = [
          ...collectedItems,
          ...(response.items || []),
        ];
        const totalItemCount = Number(response.meta?.totalItemCount || nextItems.length);

        if (nextItems.length >= totalItemCount || (response.items || []).length === 0) {
          return {
            items: nextItems,
            meta: {
              page: 1,
              itemPerPage: nextItems.length,
              totalItemCount,
            },
          };
        }

        return loadPage(page + 1, nextItems);
      });
    };

    return loadPage(1);
  },

  clearCache() {
    Object.keys(localStorage)
      .filter((key) => key.startsWith('help_content_cache_'))
      .forEach((key) => localStorage.removeItem(key));
  }
};

export const HelperProvider = ({ children }) => {
  const [cache, setCache] = useState({});
  const cacheRef = useRef({});
  const pendingRequestsRef = useRef({});

  const setCacheValue = useCallback((cacheKey, items) => {
    cacheRef.current = {
      ...cacheRef.current,
      [cacheKey]: items,
    };

    setCache(cacheRef.current);
  }, []);

  const loadHelpContentResponse = useCallback(({
    module,
    screen,
    section,
    language,
    page = 1,
    itemPerPage = 100,
    forceRefresh = false,
  } = {}) => {
    if (!localStorage.getItem('userToken')) {
      return Promise.resolve({
        items: [],
        meta: {
          page: 1,
          itemPerPage,
          totalItemCount: 0,
        },
      });
    }

    const currentLanguage = getCurrentLanguage(language);
    const cacheKey = getMemoryCacheKey({
      module,
      screen,
      section: [section, page, itemPerPage].filter(Boolean).join('_'),
      language: currentLanguage,
    });

    if (!forceRefresh && cacheRef.current[cacheKey]) {
      return Promise.resolve(cacheRef.current[cacheKey]);
    }

    if (!forceRefresh && pendingRequestsRef.current[cacheKey]) {
      return pendingRequestsRef.current[cacheKey];
    }

    const request = HelperService.getHelpContentResponse({
      module,
      screen,
      section,
      language: currentLanguage,
      page,
      itemPerPage,
      forceRefresh,
    })
      .then((response) => {
        setCacheValue(cacheKey, response);

        return response;
      })
      .catch((error) => {
        console.error('Help tartalmak betoltesi hiba:', error);
        const emptyResponse = {
          items: [],
          meta: {
            page,
            itemPerPage,
            totalItemCount: 0,
          },
        };

        setCacheValue(cacheKey, emptyResponse);

        return emptyResponse;
      })
      .finally(() => {
        delete pendingRequestsRef.current[cacheKey];
      });

    pendingRequestsRef.current[cacheKey] = request;

    return request;
  }, [setCacheValue]);

  const loadHelpContents = useCallback((params = {}) => {
    return loadHelpContentResponse(params).then((response) => response.items || []);
  }, [loadHelpContentResponse]);

  const loadAllHelpContents = useCallback(({
    module,
    screen,
    section,
    language,
    itemPerPage = 100,
    forceRefresh = false,
  } = {}) => {
    if (!localStorage.getItem('userToken')) {
      return Promise.resolve({
        items: [],
        meta: {
          page: 1,
          itemPerPage,
          totalItemCount: 0,
        },
      });
    }

    return HelperService.getAllHelpContents({
      module,
      screen,
      section,
      language: getCurrentLanguage(language),
      itemPerPage,
      forceRefresh,
    });
  }, []);

  const clearHelpCache = useCallback(() => {
    HelperService.clearCache();
    cacheRef.current = {};
    pendingRequestsRef.current = {};
    setCache({});
  }, []);

  const value = useMemo(() => ({
    cache,
    clearHelpCache,
    loadAllHelpContents,
    loadHelpContentResponse,
    loadHelpContents,
  }), [cache, clearHelpCache, loadAllHelpContents, loadHelpContentResponse, loadHelpContents]);

  return (
    <HelperContext.Provider value={value}>
      {children}
    </HelperContext.Provider>
  );
};

export const useHelpers = () => {
  return useContext(HelperContext);
};

export default HelperService;
